import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider, Box, BoxProps } from '@mui/material';
import { FlagProvider, IConfig } from '@unleash/proxy-client-react';
import { ConfirmProvider } from 'material-ui-confirm';
import { getToken, onMessage, isSupported } from 'firebase/messaging';
import TagManager from 'react-gtm-module';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import PushNotification from './components/Notification/PushNotification';
import messaging from './firebase/firebaseConfig';
import { getConfigState, getThemeMode } from './store/ConfigSlice';
import { getAuthState, getClientCredentialsToken } from './store/AuthSlice';
import { useAppSelector } from './hooks';
import { LightTheme, DarkTheme } from './theme';
import ErrorAlert from './components/Alert/ErrorAlert';
import SuccessAlert from './components/Alert/SuccessAlert';
import CustomAlerts from './components/Alert/CustomAlerts';
import FullScreenLoader from './components/Loaders/FullScreenloader';
import FeatureFlagUpdater from './components/FeatureFlag/FeatureFlagUpdater';
import BaseRoute from './BaseRoutes';
import {
  getAuthUser,
  getClientCredentialsTokenV2,
  saveFcmToken,
} from './store/AuthSlice/V2/AuthApi';
import { getTrialsState, toggleBookTrialModal } from './store/V2/TrialsSlice';
import ReportIssue from './ReportIssue';
import BookTrialModal from './pages/V2/TrialClass/modals/BookTrialModal';
import { checkUserPermission, isMobileScreen } from './helper';
import {
  CHAT_CONNECTION_MODE,
  getChatState,
  initiChatConnection,
  setConnected,
  verifyChatConnection,
} from './store/V2/ChatSlice';
import { getUserFlexiblePlans } from './store/V2/FlexiblePlansSlice';
import { getUserSubscriptionPlans } from './store/V2/SubscriptionPlansSlice';
import { ENV_TYPES } from './constants';
import { adminChatSetConnection } from './store/V2/AdminChatSlice';
import WhatsAppSupportButton from './components/Button/WhatsAppSupportButton';
import { getAppTenant, getTenantsState } from './store/V2/TenantsSlice';
import { USER_PERMISSION_INDIVIDUALS } from './store/V2/RolesSlice';

const containerStyles: BoxProps['sx'] = {
  backgroundColor: 'background.default',
  color: 'text.primary',
};

// const TRACKING_ID = process.env.REACT_APP_GOOGLE_ANALYTICS_TRACKING_ID || '';

// ReactGA.initialize(TRACKING_ID);

const featureFlagConfig: IConfig = {
  url: `${process.env.REACT_APP_FEATURE_FLAG_URL}/proxy`, // Your front-end API URL or the Unleash proxy's URL (https://<proxy-url>/proxy)
  clientKey: process.env.REACT_APP_FEATURE_FLAG_CLIENT_KEY as string, // A client-side API token OR one of your proxy's designated client keys (previously known as proxy secrets)
  refreshInterval: 0, // How often (in seconds) the client should poll the proxy for updates
  appName: process.env.REACT_APP_FEATURE_FLAG_APP_NAME as string, // The name of your application. It's only used for identifying your application
};

const App: React.FC = () => {
  const dispatch = useDispatch();

  // Store
  const configState = useAppSelector(getConfigState);
  const themeMode = useAppSelector(getThemeMode);
  const authState = useAppSelector(getAuthState);
  const trialsState = useAppSelector(getTrialsState);
  const chatState = useAppSelector(getChatState);
  const tenantState = useAppSelector(getTenantsState);

  const { user: authUser } = authState;
  const { adminEditMode } = configState;
  const { appTenant } = tenantState;
  const { selectedConversation: userChatSelectedConversation } = chatState;

  // State
  const [initialLoading, setInitiaLoading] = useState(true);
  const [isInitiated, setInitiated] = useState(false);

  const urlArr = window.location.href.split('#');
  const newUrl = new URL(`${urlArr[0]}?${window.location.hash.substr(1)}`);
  const TPAuthToken = newUrl.searchParams.get('id_token') || undefined;
  const TPAuthLoginUserId = newUrl.searchParams.get('user_id') || undefined;
  const theme = themeMode === 'light' ? LightTheme : DarkTheme;

  const isNotificationAllowed = (payload) => {
    if (payload?.notification?.title) {
      const _titleArr = payload.notification.title.split(' ');
      // DISABLE CHAT NOTIFICATION
      if (
        _titleArr.length > 1 &&
        _titleArr[0] === 'New' &&
        _titleArr[1] === 'Message' &&
        payload.data?.courseId === userChatSelectedConversation?.course
      ) {
        return false;
      }
    }
    return true;
  };

  onMessage(messaging, (payload) => {
    const isAllowed = isNotificationAllowed(payload);

    if (isAllowed) {
      toast(<PushNotification notification={payload.notification} />);
    }
  });

  useEffect(() => {
    initiate();
  }, []);

  useEffect(() => {
    initAuthUser().then();
  }, [authState.accessToken]);

  useEffect(() => {
    if (
      !isInitiated &&
      authUser &&
      appTenant &&
      authUser.role.name !== 'Guest'
    ) {
      if (
        checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            course: [USER_PERMISSION_INDIVIDUALS.COURSE_CHAT],
            adminCourse: [USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_CHAT],
          },
        })
      ) {
        dispatch(initiChatConnection());
      }

      if (
        checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            billing: [USER_PERMISSION_INDIVIDUALS.BILLING_VIEW],
          },
        })
      ) {
        dispatch(getUserFlexiblePlans());
        dispatch(getUserSubscriptionPlans());
      }

      setInitiated(true);
    }
  }, [authUser, appTenant]);

  useEffect(() => {
    if (chatState.initiated) {
      dispatch(
        verifyChatConnection({
          isAdminModeEnabled: adminEditMode,
        })
      );
    }
  }, [chatState.initiated]);

  useEffect(() => {
    if (chatState.chatConnectionMode === CHAT_CONNECTION_MODE.ADMIN) {
      dispatch(adminChatSetConnection(true));
    } else if (chatState.chatConnectionMode === CHAT_CONNECTION_MODE.USER) {
      dispatch(setConnected(true));
    } else {
      dispatch(adminChatSetConnection(false));
      dispatch(setConnected(false));
    }
  }, [chatState.chatConnectionMode]);

  const initiate = async () => {
    // ReactGA.pageview(window.location.pathname + window.location.search);
    TagManager.initialize({ gtmId: 'GTM-TZQFTRZ' });

    await dispatch(getAppTenant());

    if (TPAuthToken && TPAuthLoginUserId) {
      await dispatch(
        getClientCredentialsToken({
          auth_type: 'tutopiya',
          auth_token: TPAuthToken,
          user_id: TPAuthLoginUserId,
        })
      );
    } else if (!authState.accessToken) {
      await dispatch(getClientCredentialsTokenV2());
    }
  };

  async function requestPermission() {
    const isSupportedBrowser = await isSupported();

    if (isSupportedBrowser) {
      const serviceWorkerRegistration = await navigator.serviceWorker.register(
        `${process.env.PUBLIC_URL}/firebase-messaging-sw.js`
      );

      // requesting permission using Notification API
      const permission = await Notification.requestPermission();

      if (permission === 'granted') {
        getToken(messaging, { serviceWorkerRegistration }).then((token) => {
          dispatch(saveFcmToken({ token }));
        });
      } else if (permission === 'denied') {
        // notifications are blocked
        // eslint-disable-next-line no-console
        console.log('You denied for the notification');
      }
    }
  }

  const initAuthUser = async () => {
    if (authState.accessToken && authState.user) {
      await dispatch(getAuthUser());

      if (
        authState.user &&
        authState.user.role.name !== 'Guest' &&
        process.env.REACT_APP_ENV === ENV_TYPES.PRODUCTION
      ) {
        await requestPermission().then();
      }

      setInitiaLoading(false);

      setTimeout(() => {
        if (authState.user && authState.user.role.name !== 'Guest') {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          $zoho.salesiq.visitor.name(
            `${authState.user.firstname}${
              authState.user.lastname ? ` ${authState.user.lastname}` : ``
            } (${authState.user.role.name})`
          );
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          $zoho.salesiq.visitor.email(authState.user.email);
        }
      }, 3000);
    } else if (authState.accessToken) {
      setInitiaLoading(false);
    }
  };

  if (initialLoading) {
    return <FullScreenLoader />;
  }

  return (
    <FlagProvider config={featureFlagConfig}>
      <FeatureFlagUpdater />
      <ThemeProvider theme={theme}>
        <ConfirmProvider>
          <Box
            data-testid="main-app-container"
            className="App"
            sx={containerStyles}
          >
            <BrowserRouter>
              <Routes>
                <Route
                  path={`${process.env.PUBLIC_URL}/*`}
                  element={<BaseRoute />}
                />
              </Routes>
            </BrowserRouter>
            {/* {!isMobileScreen() && <ReportIssue />} */}
            <ErrorAlert />
            <SuccessAlert />
            <CustomAlerts />
            {trialsState.bookTrialModalOpen && (
              <BookTrialModal
                open={trialsState.bookTrialModalOpen}
                onClose={() => dispatch(toggleBookTrialModal())}
              />
            )}
          </Box>
        </ConfirmProvider>
      </ThemeProvider>
      <ToastContainer />
      <WhatsAppSupportButton />
    </FlagProvider>
  );
};

export default App;
